import getResellerCertificate, {
  QueryParameters,
} from "@faire/web-api/api/retailer/store/reseller-certificate-info/get";
import { IGetRetailerResellerCertificateFormInfoResponse } from "@faire/web-api/indigofair/data/IGetRetailerResellerCertificateFormInfoResponse";
import { IIdentityVerificationField } from "@faire/web-api/indigofair/data/IIdentityVerificationField";
import { IResellerCertificate } from "@faire/web-api/indigofair/data/IResellerCertificate";
import { THREE_LETTER_PRIMARY_COUNTRY_CODES } from "@faire/web/common/consts/THREE_LETTER_PRIMARY_COUNTRY_CODES";
import { StrictLocalizeFunction } from "@faire/web/common/localization";
import { makeObservable } from "@faire/web/common/makeObservable";
import { isNotNull } from "@faire/web/common/typescriptUtils";
import isEmpty from "lodash/isEmpty";
import omit from "lodash/omit";
import values from "lodash/values";
import { action, computed, observable } from "mobx";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";

import {
  getResellerCertificateFormData,
  IFormData,
  IIdOption,
} from "@faire/retailer/lib/getResellerCertificateFormData";
import { getSettingResellerCertificateNotifications } from "@faire/retailer/settings/getSettingResellerCertificateNotifications";
import { RetailerStore } from "@faire/retailer/stores/domain/RetailerStore";
import { ConfirmIdentityDataStorage } from "@faire/retailer/views/confirm-identity/ConfirmIdentityDataStorage";
import { ConfirmIdentityState } from "@faire/retailer/views/confirm-identity/ConfirmIdentityState";
import {
  isAStepRequired,
  CONFIRM_IDENTITY_RESELLER_INFO,
  storeFormData,
  isBasedInStateWithoutSalesTax,
} from "@faire/retailer/views/confirm-identity/utils";

const RESELLER_INFO_FIELDS: IIdentityVerificationField.Type[] = [
  IIdentityVerificationField.Type.RESELLER_ID,
];

export type IFormValues = Partial<IResellerCertificate> & {
  expiry_date_timestamp?: number;
  selected_id_type?: IIdOption;
  // TODO: Remove once BE supports this field
  registered_state?: string;
};

function getEmptyForm(): IFormValues {
  return {
    certificate_number: "",
    drivers_license_number: "",
    ein: "",
    ssn: "",
    state: undefined,
    selected_id_type: undefined,
    expiry_date_timestamp: undefined,
  };
}

export class ResellerInfoState {
  @observable
  formData?: IFormData | null;
  @observable
  dirty = false;
  @observable
  formValues: IFormValues;

  storage: ConfirmIdentityDataStorage;

  @observable
  private storeState?: string;
  @observable
  private isBasedInUSA: boolean = true;
  @observable
  basedInState?: string;

  @observable
  private storeResellerCertificateResponse?: IPromiseBasedObservable<IGetRetailerResellerCertificateFormInfoResponse>;

  constructor(
    private strictLocalize: StrictLocalizeFunction,
    private onlyCollectResellerCertificate?: boolean,
    private formState?: ConfirmIdentityState,
    private readonly inIDVFlow?: boolean,
    retailerAddressState?: string
  ) {
    makeObservable(this);
    this.formValues = getEmptyForm();
    this.storage = new ConfirmIdentityDataStorage(
      CONFIRM_IDENTITY_RESELLER_INFO,
      ""
    );

    if (this.onlyCollectResellerCertificate) {
      this.storeResellerCertificateResponse = fromPromise(
        getResellerCertificate(
          QueryParameters.build({ store_token: this.formState?.storeToken })
        )
      );
      this.storeResellerCertificateResponse.then((response) => {
        const storeRCInfo = response.store_reseller_certificate_form_info?.[0];
        const retailerRCInfo = response.retailer_reseller_certificate_form_info;

        this.isBasedInUSA =
          retailerRCInfo?.state_or_province?.includes("USA_") ?? true;
        this.basedInState = this.isBasedInUSA
          ? retailerRCInfo?.state_or_province?.replace("USA_", "")
          : undefined;

        const isStoreInUSA =
          storeRCInfo?.retailer_store_state_or_province?.includes("USA_") ??
          true;
        this.storeState = isStoreInUSA
          ? storeRCInfo?.retailer_store_state_or_province?.replace("USA_", "")
          : undefined;

        const storeRCState = storeRCInfo?.reseller_certificates[0]?.state;

        if (this.formState?.storeToken) {
          // Only prefill if states match or if the Store does not have an address.
          const shouldPrefillRC =
            this.storeState === storeRCState || !this.storeState;

          // If Store does not have a State, use previous reseller certificate's state to prefill.
          const prefilledState =
            retailerAddressState ??
            this.storeState ??
            storeRCState ??
            this.basedInState;
          const prefilledResellerCertificate = shouldPrefillRC
            ? storeRCInfo?.reseller_certificates[0]
            : undefined;

          this.formValues = {
            ...this.formValues,
            ...prefilledResellerCertificate,
            state: prefilledState,
            expiry_date_timestamp: prefilledResellerCertificate?.expires_at,
          };
        } else {
          this.formValues.state =
            retailerAddressState ??
            this.storeState ??
            storeRCState ??
            this.basedInState;
        }
      });
    }
  }

  initializeForSignup = () => {
    this.formValues.state = RetailerStore.get().geographicState;
    this.storage = new ConfirmIdentityDataStorage(
      CONFIRM_IDENTITY_RESELLER_INFO,
      RetailerStore.get().retailerToken ?? ""
    );
    if (this.inIDVFlow) {
      this.storage.initialize(JSON.stringify(this.formValues));
      if (this.storage.formData) {
        const parsedData: IFormValues | undefined | null = JSON.parse(
          this.storage.formData
        );
        if (parsedData?.state) {
          this.formValues = parsedData;
          this.setFormData(
            getResellerCertificateFormData(
              this.formValues?.state ?? "",
              this.strictLocalize
            )
          );
        }
      }
    }
  };

  @computed
  get isLoading(): boolean {
    return this.storeResellerCertificateResponse?.state === "pending";
  }

  @computed
  get storesStateMatchesResellerCertificateState(): boolean {
    return this.storeState === this.formValues.state || !this.storeState;
  }

  @computed
  get isRequired(): boolean {
    return (
      this.onlyCollectResellerCertificate ||
      (!!this.formState &&
        isAStepRequired(
          RESELLER_INFO_FIELDS,
          this.formState.requestedFieldTypes
        ))
    );
  }

  @action
  setField = <K extends keyof IFormValues, V extends IFormValues[K]>(
    key: K,
    value?: V
  ) => {
    if (key === "state") {
      this.formValues = getEmptyForm();
    }
    if (!this.formValues) {
      return;
    } else if (key === "selected_id_type") {
      (this.formData?.idOptions || []).forEach(
        (option) => (this.formValues![option.id] = "")
      );
    }
    this.formValues[key] = value;
    this.dirty = false;
    if (this.inIDVFlow) {
      storeFormData(this.storage, JSON.stringify(this.formData));
    }
  };

  private isFieldValid = (isRequired: boolean, isValid: boolean) => {
    if (!isRequired) {
      return true;
    }
    return isValid;
  };

  private isFiveOrMoreCharacters = (value?: string) =>
    !!value && value.length >= 5;

  private isNOrLessCharacters = (n: number, value?: string) =>
    !!value && value.length <= n;

  @computed
  get isBasedInStateWithoutSalesTax() {
    if (!getSettingResellerCertificateNotifications()) {
      return false;
    }
    return isBasedInStateWithoutSalesTax(this.basedInState, this.isBasedInUSA);
  }

  @computed
  get isShippingToAnotherStateWithSalesTax() {
    if (!getSettingResellerCertificateNotifications()) {
      return false;
    }
    const shipToState = this.formValues.state;
    return (
      !!shipToState &&
      !!this.basedInState &&
      shipToState !== this.basedInState &&
      !this.isBasedInStateWithoutSalesTax
    );
  }

  @computed
  private get isResellerCertificateNumberValid(): boolean {
    if (this.isBasedInStateWithoutSalesTax) {
      return true;
    }
    return this.isFieldValid(
      !!this.formData?.taxAccountIdNumber ||
        (this.formData?.idOptions?.length === 1 &&
          this.formData?.idOptions[0]?.id === "certificate_number") ||
        this.formValues.selected_id_type?.id === "certificate_number",
      this.isFiveOrMoreCharacters(this.formValues.certificate_number) &&
        this.isNOrLessCharacters(100, this.formValues.certificate_number) &&
        (this.formValues.state === "NY" &&
        getSettingResellerCertificateNotifications()
          ? isValidNYTaxId(this.formValues.certificate_number)
          : true)
    );
  }

  private get isConfirmedNotEligibleForCertificateValid(): boolean {
    return this.isFieldValid(
      this.isBasedInStateWithoutSalesTax,
      !!this.formValues.confirmed_not_eligible_for_certificate
    );
  }

  @computed
  private get isDriversLicenceValid(): boolean {
    return this.isFieldValid(
      this.formValues?.selected_id_type?.id === "drivers_license_number",
      this.isFiveOrMoreCharacters(this.formValues.drivers_license_number)
    );
  }

  @computed
  private get isFeinValid(): boolean {
    return this.isFieldValid(
      this.formValues?.selected_id_type?.id === "ein",
      this.isFiveOrMoreCharacters(this.formValues.ein) &&
        this.isNOrLessCharacters(100, this.formValues.ein)
    );
  }

  @computed
  private get isSsnValid(): boolean {
    return this.isFieldValid(
      this.formValues?.selected_id_type?.id === "ssn",
      this.isFiveOrMoreCharacters(this.formValues.ssn) &&
        this.isNOrLessCharacters(64, this.formValues.ssn)
    );
  }

  @computed
  private get isExpiryDateValid(): boolean {
    const expiryDate = this.formValues?.expiry_date_timestamp;
    const expirationDateRequired = !!this.formData?.expirationDateRequired;

    if (!expirationDateRequired) {
      return true;
    }

    return this.isFieldValid(
      (this.formValues?.certificate_number?.length ?? 0) > 0,
      !!expiryDate
    );
  }

  @computed
  private get isIdTypeValid(): boolean {
    return this.isFieldValid(
      !!this.formData?.idOptions?.length &&
        this.formData?.idOptions?.length > 1,
      !!this.formValues?.selected_id_type
    );
  }

  @computed
  get isStateSelected(): boolean {
    return !!this.formValues.state;
  }

  @computed
  get isResellerInfoRequired(): boolean {
    return !!this.formData;
  }

  @computed
  private get errors(): { [key: string]: boolean } {
    return {
      certificate_number: !this.isResellerCertificateNumberValid,
      drivers_license_number: !this.isDriversLicenceValid,
      ein: !this.isFeinValid,
      ssn: !this.isSsnValid,
      expiry_date_timestamp: !this.isExpiryDateValid,
      id_type: !this.isIdTypeValid,
      confirmed_not_eligible_for_certificate:
        !this.isConfirmedNotEligibleForCertificateValid,
    };
  }

  @computed
  private get onlyStateAndTypeSelected(): boolean {
    return Object.keys(this.nonEmptyFormValues).every(
      (key) => key === "state" || key === "selected_id_type"
    );
  }

  @computed
  private get inSignUpFlowOptional(): boolean {
    return !this.inIDVFlow && this.onlyStateAndTypeSelected;
  }

  @computed
  get isFormFieldsValid() {
    return !values(this.errors).includes(true);
  }

  @computed
  get isValid() {
    return this.isFormFieldsValid || this.inSignUpFlowOptional;
  }

  @computed
  get noEmptyFields(): boolean {
    if (
      this.formState?.retailerCountry !== THREE_LETTER_PRIMARY_COUNTRY_CODES.USA
    ) {
      return true;
    }
    // check all error fields and see if the error is caused by empty value
    // need to exclude "id_type" since this error cannot be caused by empty
    const errorFields = Object.entries(this.errors)
      .map(([field, hasError]) =>
        hasError && field !== "id_type" ? field : null
      )
      .filter(isNotNull);

    const emptyErrorFields = errorFields.filter((field) =>
      // @ts-expect-error FIXME(implicitAny): https://faire.link/no-implicit-any
      isEmpty(this.formValues[field])
    );

    return emptyErrorFields.length === 0;
  }

  showErrorEvenIfOptional = (key: string): boolean => {
    return (this.dirty && this.errors[key]) ?? false;
  };

  showError = (key: string): boolean => {
    return (
      (this.dirty && this.errors[key] && !this.inSignUpFlowOptional) ?? false
    );
  };

  @action
  setDirty = () => {
    this.dirty = true;
  };

  @action
  setFormData = (value?: IFormData | null) => {
    this.formData = value;
  };

  @computed
  private get nonEmptyFormValues(): IFormValues {
    return Object.keys(this.formValues).reduce((acc, key) => {
      // @ts-expect-error FIXME(implicitAny): https://faire.link/no-implicit-any
      if (this.formValues[key]) {
        // @ts-expect-error FIXME(implicitAny): https://faire.link/no-implicit-any
        acc[key] = this.formValues[key];
      }
      return acc;
    }, {} as IFormValues);
  }

  @computed
  get payload(): Partial<IResellerCertificate> {
    return {
      ...omit(
        this.nonEmptyFormValues,
        "expiry_date_timestamp",
        "selected_id_type"
      ),
      expires_at: this.formValues.expiry_date_timestamp ?? undefined,
    };
  }

  loadDraftIdv = () => {
    const resaleCertificate =
      this.formState?.existingIDVDraft?.reseller_certificate;
    if (!resaleCertificate) {
      return;
    }

    this.setFormData(
      getResellerCertificateFormData(
        resaleCertificate.state ?? "",
        this.strictLocalize
      )
    );
    this.formValues = {
      ...resaleCertificate,
      expiry_date_timestamp: resaleCertificate.expires_at,
    };
  };
}

const isValidNYTaxId = (taxId: string | undefined) => {
  if (!taxId) {
    return false;
  }
  return /(?:\d{2}-?\d{7}|[tT][fF]-?\d{7})/.test(taxId);
};
